import { useCallback, useState } from "react";
import { t } from "ttag";
import _ from "underscore";

import { StrategyForm } from "metabase/admin/performance/components/StrategyForm";
import { useCacheConfigs } from "metabase/admin/performance/hooks/useCacheConfigs";
import { useConfirmIfFormIsDirty } from "metabase/admin/performance/hooks/useConfirmIfFormIsDirty";
import { useInvalidateTarget } from "metabase/admin/performance/hooks/useInvalidateTarget";
import { useSaveStrategy } from "metabase/admin/performance/hooks/useSaveStrategy";
import { DelayedLoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper/DelayedLoadingAndErrorWrapper";
import { Sidesheet } from "metabase/common/components/Sidesheet";
import { useConfirmation } from "metabase/common/hooks/use-confirmation";
import { Button, Flex, type ModalOverlayProps, Stack } from "metabase/ui";
import type Question from "metabase-lib/v1/Question";
import type {
  CacheStrategy,
  CacheableDashboard,
  CacheableModel,
} from "metabase-types/api";

import { type SidebarPageSetter, getItemId, getItemName } from "./shared";

export const SidebarCacheSection = ({
  item,
  model,
  setPage,
}: {
  item: CacheableDashboard | Question;
  model: CacheableModel;
  setPage: SidebarPageSetter;
}) => {
  const id = getItemId(model, item);
  const { configs, isLoading, error } = useCacheConfigs({ model: [model], id });
  const targetConfig = _.findWhere(configs ?? [], { model, model_id: id });

  return (
    <DelayedLoadingAndErrorWrapper delay={0} loading={isLoading} error={error}>
      <Flex align="center" justify="space-between">
        <span>{t`When to get new results`}</span>
        <Button variant="subtle" p={0} onClick={() => setPage("caching")}>
          {targetConfig?.strategy?.type === "duration"
            ? `${targetConfig.strategy.duration}${targetConfig.strategy.unit}`
            : t`Use default`}
        </Button>
      </Flex>
    </DelayedLoadingAndErrorWrapper>
  );
};

export const SidebarCacheForm = ({
  item,
  model,
  isOpen,
  onClose,
  onBack,
  withOverlay = true,
  overlayProps,
  ...stackProps
}: {
  item: CacheableDashboard | Question;
  model: CacheableModel;
  isOpen: boolean;
  withOverlay?: boolean;
  overlayProps?: ModalOverlayProps;
  onClose: () => void;
  onBack: () => void;
  [key: string]: unknown;
}) => {
  const id = getItemId(model, item);
  const { configs, isLoading, error } = useCacheConfigs({ model: [model], id });
  const targetConfig = _.findWhere(configs ?? [], { model_id: id, model });
  const saveStrategy = useSaveStrategy(id, model);
  const saveAndBack = useCallback(
    async (values: CacheStrategy) => {
      await saveStrategy(values);
      onBack();
    },
    [onBack, saveStrategy],
  );
  const {
    confirmationModal,
    setIsStrategyFormDirty,
    isStrategyFormDirty,
    askBeforeDiscardingChanges,
  } = useConfirmIfFormIsDirty();

  return (
    <Sidesheet
      title={t`Caching settings`}
      isOpen={isOpen}
      onClose={() =>
        isStrategyFormDirty ? askBeforeDiscardingChanges(onClose) : onClose()
      }
      withOverlay={withOverlay}
      overlayProps={overlayProps}
      closeOnEscape
    >
      <Stack {...stackProps}>
        <DelayedLoadingAndErrorWrapper loading={isLoading} error={error}>
          <StrategyForm
            targetId={id}
            targetModel={model}
            targetName={getItemName(model, item)}
            setIsDirty={setIsStrategyFormDirty}
            saveStrategy={saveAndBack}
            savedStrategy={targetConfig?.strategy}
            shouldAllowInvalidation
            shouldShowName={false}
            onReset={onBack}
            buttonLabels={{ save: t`Save`, discard: t`Cancel` }}
            isInSidebar
          />
        </DelayedLoadingAndErrorWrapper>
        {confirmationModal}
      </Stack>
    </Sidesheet>
  );
};

export const InvalidateNowButton = ({
  targetId,
  targetModel,
  targetName,
}: {
  targetId: number;
  targetModel: CacheableModel;
  targetName: string;
}) => {
  const invalidateTarget = useInvalidateTarget(targetId, targetModel);
  const { show: askConfirmation, modalContent } = useConfirmation();
  const [status, setStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");

  const confirmInvalidation = useCallback(() => {
    askConfirmation({
      title: targetName
        ? t`Clear all cached results for ${targetName}?`
        : t`Clear all cached results for this object?`,
      confirmButtonText: t`Clear cache`,
      onConfirm: async () => {
        try {
          setStatus("pending");
          await invalidateTarget();
          setStatus("success");
        } catch {
          setStatus("error");
        }
      },
    });
  }, [askConfirmation, invalidateTarget, targetName]);

  return (
    <>
      <Button
        color="danger"
        variant="subtle"
        onClick={confirmInvalidation}
        loading={status === "pending"}
      >
        {status === "success" ? t`Cache cleared` : t`Clear cache`}
      </Button>
      {modalContent}
    </>
  );
};
